import LibraryIcon from './LibraryIcon';
import { isLibraryIconName } from '../../utils/libraryIcons';

/**
 * Choice elements, as antd Select options — one renderer, every surface.
 *
 * WHICH elements: a Choice with a list, and nothing else (`rendersAsChoice`).
 * Choices live only on choice elements (the owner's ruling, 2026-10-19; KPE-033).
 * `options.choices` SURVIVES a change of type — the card editor keeps the list
 * so a change back to Choice can reuse it — and on any other type it is inert:
 * a short text, number, rating or date that carries one keeps its own text box,
 * `>` and `<`, or picker. (Until KPR-1015 a short text with a list read as a
 * choice, the legacy construct the migration KPR-1012 converted away.)
 *
 * A choice may carry an `icon` (an export name from components/icons) and a
 * `color`; the item form renders both, so the automation builders do too — a
 * status the user knows as a red dot must not become a bare word here.
 *
 * And a `help`: the choice's own hint (KPR-1976), what picking it means, beside
 * the element's help text. It is a muted line under the label in the open
 * list only — the closed field and the picked chips show the label alone
 * (css/components/builders.css) — and search still reads the label.
 */

// The predicates are pure and live in utils/choiceValues (KPR-142), so the list
// filter builder's model can ask the same question; every component keeps
// importing them from here.
export { elementHasChoices, rendersAsChoice } from '../../utils/choiceValues';

const choiceIcon = choice => {
	if ( choice?.iconJSX ) return choice.iconJSX;
	// A library icon, drawn on demand (KPR-1187); nothing for a name the library does not have.
	return isLibraryIconName( choice?.icon ) ? <LibraryIcon name={ choice.icon } /> : null;
};

/** A choice's own hint, trimmed; '' when it has none or it is not text. */
export const choiceHintOf = choice => ( typeof choice?.help === 'string' ? choice.help.trim() : '' );

const choiceText = ( text, hint ) => ( hint ? (
	<span className='choice-option__text'>
		<span className='choice-option__label'>{text}</span>
		<span className='choice-option__hint'>{hint}</span>
	</span>
) : <span className='choice-option__label'>{text}</span> );

/**
 * A card def's `options.choices` -> Select options. Labels are ReactNodes, so
 * each carries `searchText` for the platform's optionSearchFilter (antd's own
 * filter stringifies the value and would match nothing).
 */
export function buildChoiceOptions( choices = [] ) {
	return ( Array.isArray( choices ) ? choices : [] ).map( choice => {
		const value = choice?.value ?? choice?.label;
		const text = choice?.label ?? `${ choice?.value ?? '' }`;
		const icon = choiceIcon( choice );
		return {
			value,
			label: (
				<span className='choice-option'>
					{choice?.color ? (
						<span className='choice-option__swatch' style={{ backgroundColor: choice.color }} />
					) : null}
					{icon ? <span className='choice-option__icon'>{icon}</span> : null}
					{choiceText( text, choiceHintOf( choice ) )}
				</span>
			),
			searchText: `${ text }`,
		};
	} );
}

/**
 * The single dropdown's options (the item form's plain Select, which shows a
 * label alone): a choice with a hint gets its label and hint as two lines and a
 * `searchText` of its label; every other choice is handed back untouched, so a
 * list with no hints renders exactly as it did.
 */
export function withChoiceHints( choices = [] ) {
	return ( Array.isArray( choices ) ? choices : [] ).map( choice => {
		const hint = choiceHintOf( choice );
		if ( ! hint ) return choice;
		const text = choice?.label ?? `${ choice?.value ?? '' }`;
		return {
			...choice,
			label: <span className='choice-option'>{choiceText( text, hint )}</span>,
			searchText: `${ text }`,
		};
	} );
}

export default buildChoiceOptions;
